import {
  EntityType,
  EntryType,
  InteractionDirection,
  InteractionType,
} from '@maximizer/core/shared/domain';
import {
  TimelineFilter,
  TimelineKeys,
} from '@maximizer/core/timeline/data-access';
import { TranslateService, Translation } from '@ngx-translate/core';
import { HtmlBuilder } from './html';

interface SummaryItem {
  dateTime: string;
  content: string;
}

interface SummaryHighlight {
  theme: string;
  items: string[];
}

interface SummaryRecommendation {
  type: string;
  title: string;
  content: string;
}

interface SummaryOutstandingAndRisks {
  outstanding: string[];
  risks: string[];
}

export interface Summary {
  dealContext: string[];
  keyMilestoneAndTimelines: SummaryItem[];
  highlightsOfPastCommunications: SummaryHighlight[];
  recentCommunicationsSummary: SummaryItem[];
  outstandingItemsAndRisks: SummaryOutstandingAndRisks;
  recommendations: SummaryRecommendation[];
  sentimentAnalysis: string[];
}

export const TimelineSummaryPrompt =
  'You are an assistant tasked with analyzing a set of communications (calls, emails, and other interactions). Generate a comprehensive yet concise summary using only the provided information. Create a response in JSON format with the following structure { "dealContext": ["Briefly summarize the deal, including the product/service discussed, the client’s primary goals, and key stakeholders involved"], "keyMilestoneAndTimelines": [{ "dateTime": The date and time (keep source format), "content": The summary }], "highlightsOfPastCommunications": [{ "theme", "items" }] "Group and summarize the discussions by theme (e.g. pricing, technical requirements, legal/contract negotiations), provide one to three items for each theme (empty if not enough information is available). Note how the conversation has evolved over the months, including any shifts in priorities or tone.", "recentCommunicationsSummary": [{ "dateTime": The date and time, "content": The summary }] "Provide details on the last 2-3 interactions including: Topics discussed, decisions made, any unresolved questions or next steps identified.", "outstandingItemsAndRisks": { outstanding: [], risks: [] } "Identify unresolved questions, pending approvals, or overdue deliverables. Note any risks or potential challenges that could affect the deal", "recommendations": [{ "type": "Email or Task or Meeting", "title", "content" }] "Provide one to three recommendations", "sentimentAnalysis": ["Sentiment analysis if possible to indicate changes in tone or satisfaction over time, provide one to three."]}';

export class TimelineSummaryHtmlBuilder extends HtmlBuilder {
  titles: {
    dealContext: string;
    keyMilestoneAndTimelines: string;
    highlightsOfPastCommunications: string;
    recentCommunicationsSummary: string;
    outstandingItemsAndRisks: string;
    outstandingItems: string;
    risks: string;
    recommendedActions: string;
    sentimentAnalysis: string;
  };

  constructor(
    private readonly summary: Summary,
    translate: TranslateService,
    private readonly sectionPrefix = '',
  ) {
    super(translate);

    this.titles = {
      dealContext: this.translateTitle('dealContext'),
      keyMilestoneAndTimelines: this.translateTitle(
        'keyMilestonesAndTimelines',
      ),
      highlightsOfPastCommunications: this.translateTitle(
        'highlightsOfPastCommunications',
      ),
      recentCommunicationsSummary: this.translateTitle(
        'recentCommunicationsSummary',
      ),
      outstandingItemsAndRisks: this.translateTitle(
        'outstandingItemsAndRisks.title',
      ),
      outstandingItems: this.translateTitle(
        'outstandingItemsAndRisks.outstandingItems',
      ),
      risks: this.translateTitle('outstandingItemsAndRisks.risks'),
      recommendedActions: this.translateTitle('recommendedActions'),
      sentimentAnalysis: this.translateTitle('sentimentAnalysis'),
    };
  }

  private translateTitle(key: string): string {
    const keyPrefix = this.sectionPrefix ? `${this.sectionPrefix}.` : '';
    return this.translate.instant(`${keyPrefix}summarization.sections.${key}`);
  }

  dealContext(): this {
    if (this.summary?.dealContext?.length) {
      this.title(this.titles.dealContext);

      this.summary.dealContext.forEach((item) => {
        this.paragraph(item, this.neutral70).breakLine();
      });
    }
    return this;
  }

  keyMilestoneAndTimelines(): this {
    if (this.summary?.keyMilestoneAndTimelines?.length) {
      return this.mapSummaryItems(
        this.titles.keyMilestoneAndTimelines,
        this.summary.keyMilestoneAndTimelines,
      );
    }
    return this;
  }

  highlightsOfPastCommunications(): this {
    if (
      this.summary?.highlightsOfPastCommunications?.length &&
      this.summary?.highlightsOfPastCommunications.some(
        (item) => item.items.length,
      )
    ) {
      return this.title(this.titles.highlightsOfPastCommunications)
        .numberedList(
          this.summary.highlightsOfPastCommunications
            .filter((item) => item.items.length)
            .map((item) => this.mapDetail(item.theme, item.items)),
        )
        .breakLine();
    }
    return this;
  }

  recentCommunicationsSummary(): this {
    if (this.summary?.recentCommunicationsSummary?.length) {
      return this.mapSummaryItems(
        this.titles.recentCommunicationsSummary,
        this.summary.recentCommunicationsSummary,
      );
    }
    return this;
  }

  outstandingItemsAndRisks(): this {
    if (
      this.summary?.outstandingItemsAndRisks?.outstanding?.length ||
      this.summary?.outstandingItemsAndRisks?.risks?.length
    ) {
      const items: string[] = [];

      if (this.summary.outstandingItemsAndRisks.outstanding?.length) {
        items.push(
          this.mapDetail(
            this.titles.outstandingItems,
            this.summary.outstandingItemsAndRisks.outstanding,
          ),
        );
      }

      if (this.summary.outstandingItemsAndRisks.risks?.length) {
        items.push(
          this.mapDetail(
            this.titles.risks,
            this.summary.outstandingItemsAndRisks.risks,
          ),
        );
      }

      return this.title(this.titles.outstandingItemsAndRisks)
        .numberedList(items)
        .breakLine();
    }
    return this;
  }

  recommendedActions(): this {
    if (this.summary?.recommendations?.length) {
      return this.title(this.titles.recommendedActions)
        .numberedList(
          this.summary.recommendations.map((item) =>
            this.mapDetail(item.title, [item.content]),
          ),
        )
        .breakLine();
    }
    return this;
  }

  sentimentAnalysis(): this {
    if (this.summary?.sentimentAnalysis?.length) {
      return this.title(this.titles.sentimentAnalysis)
        .bulletList(
          this.summary.sentimentAnalysis.map((item) =>
            this.tag('p', item, this.noMargin, this.neutral70),
          ),
        )
        .breakLine();
    }
    return this;
  }

  private mapSummaryItems(title: string, items?: SummaryItem[]): this {
    if (items?.length) {
      return this.title(title)
        .numberedList(
          items.map((item) => this.mapDetail(item.dateTime, [item.content])),
        )
        .breakLine();
    }
    return this;
  }

  private mapDetail(header: string, details: string[]): string {
    return `${this.tag('p', header + ':', this.noMargin)} ${this.list(
      'ul',
      details.map((item) => this.tag('p', item, this.noMargin, this.neutral70)),
      this.discList,
      this.noMargin,
      this.listPadding,
    )}`;
  }
}

export interface TimelineSummaryArgs {
  key: string | TimelineKeys;
  type: EntryType;
  filter: TimelineFilter;
}

export interface RefreshArgs {
  type?: EntryType;
  key?: string;
  newKey?: string;
}

export type RecommendedAction = {
  icon: string;
  entity: EntityType;
  translation: Translation;
  interaction?: InteractionType;
  direction?: InteractionDirection;
};
